import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  LinearProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import { AccountBalance as BankIcon, Search as SearchIcon } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import SortableTableCell from './SortableTableCell';
import TransactionRow from './TransactionRow';
import EmptyState from './EmptyState';
const TransactionsPanel = ({
  filteredTransactions,
  selectedTransactions,
  selectedJournalEntries,
  transactionTypeFilter,
  transactionSortBy,
  transactionSortOrder,
  onTransactionToggle,
  onSelectAllTransactions,
  areAllTransactionsSelected,
  onSortChange,
  isLoadingTransactions,
  transactionSearch,
  onTransactionSearchChange,
  reconciliationFilter, // Add filter indicator
  onUnreconcile, // Add unreconcile function
  onCreateEntry, // Add create entry function
  onBulkReconcile, // Bulk 1:1 reconcile by value
  onBulkCreateEntries, // Bulk create entries from multiple transactions
}) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const toggleSort = (sortBy) => {
    onSortChange(sortBy);
  };

  return (
    <Box
      sx={{
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Left side - Title and Count */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <BankIcon fontSize="small" />
              <Typography variant="subtitle2" fontWeight="600">
                Extratos Bancários
              </Typography>
              <Chip
                label={filteredTransactions.length}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: alpha(theme.palette.common.white, 0.25),
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  minWidth: 28,
                  '& .MuiChip-label': {
                    px: 0.75,
                  },
                }}
              />
            </Stack>

            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar extratos"
                value={transactionSearch}
                onChange={(e) => onTransactionSearchChange(e.target.value)}
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                  minHeight: 36,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '0.8rem',
                    height: 36,
                  },
                }}
              />
            </Box>

            {/* Right side - Filter, Selection indicators and Bulk Action */}
            <Stack direction="row" spacing={1} alignItems="center">

              {/* Filter indicator */}
              {reconciliationFilter === 'unreconciled' && (
                <Chip
                  label="Apenas não conciliadas"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.warning.main, 0.9),
                    color: 'white',
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                />
              )}
              {reconciliationFilter === 'reconciled' && (
                <Chip
                  label="Apenas conciliadas"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.success.main, 0.9),
                    color: 'white',
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                />
              )}
              {reconciliationFilter === 'all' && (
                <Chip
                  label="Todos os extratos"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.info.main, 0.9),
                    color: 'white',
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                />
              )}

              {selectedTransactions.length > 0 && (
                <Chip
                  label={`${selectedTransactions.length} selecionadas`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.25),
                    color: 'white',
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    border: '1px solid rgba(255,255,255,0.3)',
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Table Container with proper scrolling */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: '60vh', // Limit maximum height for better UX
            minHeight: 0,
          }}
        >
          {isLoadingTransactions ? (
            <LinearProgress sx={{ flexShrink: 0 }} />
          ) : (
            <TableContainer
              component={Box}
              sx={{
                height: '100%',
                overflow: 'auto',
                minHeight: 0,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#a1a1a1',
                  },
                },
                '&::-webkit-scrollbar-thumb:active': {
                  background: '#919191',
                },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ height: 48 }}>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        backgroundColor: '#f8fafc',
                        height: 48,
                        padding: '8px 12px',
                        borderBottom: '2px solid #e0e0e0',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={areAllTransactionsSelected}
                        indeterminate={
                          selectedTransactions.length > 0 &&
                          selectedTransactions.length < filteredTransactions.length
                        }
                        onChange={(e) => onSelectAllTransactions(e.target.checked)}
                      />
                    </TableCell>
                    <SortableTableCell
                      label="Data"
                      sortBy="date"
                      currentSortBy={transactionSortBy}
                      currentSortOrder={transactionSortOrder}
                      onSort={toggleSort}
                      tooltip="Ordenar por data (nova/antiga)"
                      align="center"
                      sx={{ width: 80, maxWidth: 80, minWidth: 80 }}
                    />
                    <SortableTableCell
                      label="Descrição"
                      sortBy="description"
                      currentSortBy={transactionSortBy}
                      currentSortOrder={transactionSortOrder}
                      onSort={toggleSort}
                      tooltip="Ordenar por descrição (A-Z/Z-A)"
                      sx={{ minWidth: 200 }}
                    />
                    <SortableTableCell
                      label="Valor"
                      sortBy="amount"
                      currentSortBy={transactionSortBy}
                      currentSortOrder={transactionSortOrder}
                      onSort={toggleSort}
                      tooltip="Ordenar por valor (maior/menor)"
                      align="right"
                      sx={{ minWidth: 100 }}
                    />
                    <SortableTableCell
                      label="Status"
                      sortBy="status"
                      currentSortBy={transactionSortBy}
                      currentSortOrder={transactionSortOrder}
                      onSort={toggleSort}
                      tooltip="Ordenar por status (conciliada/pendente)"
                      align="center"
                      sx={{ minWidth: 80 }}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((transaction) => (
                      <TransactionRow
                        key={transaction.transactionHash}
                        transaction={transaction}
                        isSelected={selectedTransactions.some(
                          (t) => t.transactionHash === transaction.transactionHash
                        )}
                        onToggle={onTransactionToggle}
                        formatDate={formatDate}
                        onUnreconcile={onUnreconcile}
                        onCreateEntry={onCreateEntry}
                      />
                    ))
                  ) : (
                    <EmptyState
                      type="transactions"
                      isLoading={isLoadingTransactions}
                      searchTerm={transactionSearch}
                      filterType={transactionTypeFilter}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default TransactionsPanel;
